import { Injectable, NgZone } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Subscription, finalize, timeout, timer } from 'rxjs'
import { Platform, ToastController } from '@ionic/angular'
import Intercom, { boot, showSpace, shutdown, update, onShow, onHide } from '@intercom/messenger-js-sdk'

declare let window: any

import { ENV_CONFIG } from 'src/bin/env.config'
import { Salon } from '../models/salon/salon.model'
import { Stylist } from '../models/stylist/stylist.model'
import { ChatPlatform, buildChatCustomAttributes, getFilteredJobTitle } from './chat-attributes'

/**
 * Intercom workspace id — same workspace as the pre-2026 integration
 * (the old `window.Intercom('boot', { app_id: 'p3f80lct', ... })` call in
 * UserService).
 */
const INTERCOM_APP_ID = 'p3f80lct'
const INTERCOM_REGION = 'us'

/**
 * Seconds of slack when checking a JWT's `exp`: a token that expires within
 * this window is treated as already expired, so we never hand Intercom a
 * token that dies mid-handshake.
 */
const JWT_EXPIRY_MARGIN_SECONDS = 30

/**
 * The only keys shutdown() leaves on `window.intercomSettings`. Everything the
 * SDK copies there at boot and never removes — the identity credential, the
 * name and email, and every custom attribute (phone numbers, pronouns, join
 * date) — is deleted, so a logged-out user's details are not readable on a
 * global for the rest of the page's life. This is a keep-list on purpose: the
 * previous deny-list of six identity keys silently fell behind each time the
 * attribute payload grew. `app_id`/`api_base` must survive (a pending widget
 * script reads app_id off the global) and `installation_type` is the SDK's own.
 */
const PRESERVED_SETTING_KEYS = [
  'app_id',
  'api_base',
  'installation_type',
  // Not identity, but the app never wants a visible launcher: if the user logs
  // out before the widget script finishes loading, the widget boots from
  // whatever is left on this global, and without this it would appear.
  'hide_default_launcher'
]

/**
 * Messenger settings passed to the SDK's boot call. Only the fields we always
 * send are typed strictly; everything else (custom attributes, identity
 * fields) goes through the index signature.
 */
interface IntercomBootSettings {
  app_id: string
  region: string
  user_id: string
  hide_default_launcher: boolean
  [key: string]: unknown
}

/**
 * Thin wrapper around `@intercom/messenger-js-sdk` so pages and services
 * depend on a typed Angular service instead of touching `window.Intercom`.
 *
 * Mirrors the PostHogService philosophy: every SDK call is wrapped in
 * try/catch — a chat failure must never break login, logout, or navigation.
 *
 * Identity verification (JWT vs hash):
 * - `intercomUserJwt` is runtime-only — it comes down on the login/session
 *   payload and is deliberately never persisted to localStorage. When it is
 *   present and unexpired we send `intercom_user_jwt`.
 * - Restored sessions request a fresh signed JWT using the app's authenticated
 *   session. The persisted user_hash is a compatibility fallback while the API
 *   rolls out or is temporarily unavailable.
 * - We never send both: Intercom treats the JWT as authoritative and sending
 *   a stale hash alongside it can fail verification.
 *
 * Intercom is the app's only help chat: every identifiable login boots the
 * messenger (launcher hidden) and the app's Help buttons open it via show().
 */
@Injectable({ providedIn: 'root' })
export class IntercomService {
  private booted = false

  private session?: { user: Stylist; salon: Salon; token: string }
  private generation = 0
  private refreshRequest?: Subscription
  private refreshTimer?: Subscription
  private refreshFailures = 0
  private lastRefresh = 0
  private pendingShow = false
  private jwtSession = false
  private identityDenied = false
  private messengerVisible = false
  private listenersGeneration = -1
  private widgetGeneration = 0
  private showTimer?: Subscription

  constructor (
    private readonly platform: Platform,
    private readonly http: HttpClient,
    private readonly toast: ToastController,
    private readonly zone: NgZone
  ) {}

  /** A fresh JWT is fetched on both password login and localStorage restore. */
  public startSession (user: Stylist, salon: Salon, token: string): void {
    if (!this.shutdown()) return
    this.session = { user, salon, token }
    this.refreshIdentity()
  }

  private refreshIdentity (): void {
    const session = this.session
    if (!session?.token || this.refreshRequest) return
    this.refreshTimer?.unsubscribe()
    this.refreshTimer = undefined
    const generation = this.generation
    // Set the holder before subscribing: synchronous failures must not leave a stale in-flight marker.
    const holder = new Subscription()
    this.refreshRequest = holder
    holder.add(this.http.get<{ data: { intercomUserJwt: string } }>(
      `${ENV_CONFIG.API_ROOT}/stylists/me/intercom-session`,
      {
        headers: new HttpHeaders({ Authorization: session.token }),
        params: { platform: this.currentPlatform(), app_build_number: ENV_CONFIG.appVersion }
      }
    ).pipe(
      timeout(10000),
      finalize(() => {
        if (this.generation === generation) this.refreshRequest = undefined
      })
    ).subscribe({
      next: response => {
        if (this.generation !== generation) return
        const jwt = response?.data?.intercomUserJwt
        let claims: Record<string, any>
        try {
          claims = JSON.parse(this.base64UrlDecode(jwt.split('.')[1]))
          if (claims.user_id !== String(session.user.getID()) || this.isJwtExpired(jwt)) throw new Error('Invalid chat identity')
        } catch {
          this.refreshFailed()
          return
        }
        try {
          if (this.booted && this.jwtSession) {
            this.sdkUpdate({ intercom_user_jwt: jwt })
          } else {
            // Switching from a legacy hash must clear it before a JWT is sent.
            this.pendingShow = this.pendingShow || this.messengerVisible || !!(this.showTimer && !this.showTimer.closed)
            this.resetMessengerState()
            this.sdkShutdown()
            this.scrubIdentitySettings()
            this.bootMessenger(session.user, session.salon, jwt)
          }
          this.lastRefresh = Date.now()
          this.refreshFailures = 0
          this.refreshTimer?.unsubscribe()
          this.zone.runOutsideAngular(() => {
            const remaining = claims.exp * 1000 - Date.now()
            const delay = Math.max(1000, remaining - Math.min(5 * 60 * 1000, remaining / 2))
            this.refreshTimer = timer(delay).subscribe(() => {
              if (this.generation === generation) this.refreshIdentity()
            })
          })
          if (this.pendingShow) {
            this.pendingShow = false
            this.openMessenger()
          }
        } catch (err) {
          console.error('[IntercomService] refresh failed', err)
          this.booted = false
          this.jwtSession = false
          this.scrubIdentitySettings()
          if (this.pendingShow) {
            this.pendingShow = false
            void this.showUnavailableNotice()
          }
        }
      },
      error: error => {
        if (this.generation !== generation) return
        if (error?.status === 401 || error?.status === 403) {
          const requested = this.pendingShow
          this.shutdown()
          this.identityDenied = true
          if (requested) void this.showUnavailableNotice()
        } else {
          this.refreshFailed()
        }
      }
    }))
  }

  private refreshFailed (): void {
    this.scheduleRefreshRetry()
    if (!this.booted && this.session && this.canIdentify(this.session.user)) {
      try {
        this.bootMessenger(this.session.user, this.session.salon)
      } catch (err) {
        console.error('[IntercomService] fallback boot failed', err)
      }
    }
    if (this.pendingShow) {
      this.pendingShow = false
      if (this.booted) this.openMessenger()
      else void this.showUnavailableNotice()
    }
  }

  private scheduleRefreshRetry (): void {
    if (!this.session?.token) return
    const generation = this.generation
    const delay = Math.min(10000 * 2 ** Math.min(this.refreshFailures++, 5), 5 * 60 * 1000)
    this.refreshTimer?.unsubscribe()
    this.zone.runOutsideAngular(() => {
      this.refreshTimer = timer(delay).subscribe(() => {
        if (this.generation === generation) this.refreshIdentity()
      })
    })
  }

  /** Invalidate callbacks belonging to a widget destroyed by shutdown/reboot. */
  private resetMessengerState (): void {
    this.widgetGeneration++
    this.showTimer?.unsubscribe()
    this.showTimer = undefined
    this.messengerVisible = false
    this.listenersGeneration = -1
  }

  private openMessenger (): void {
    try {
      const generation = this.widgetGeneration
      if (this.listenersGeneration !== generation) {
        this.sdkOnShow(() => {
          if (generation !== this.widgetGeneration) return
          this.messengerVisible = true
          this.showTimer?.unsubscribe()
        })
        this.sdkOnHide(() => {
          if (generation === this.widgetGeneration) this.messengerVisible = false
        })
        this.listenersGeneration = generation
      }
      this.showTimer?.unsubscribe()
      if (!this.messengerVisible && this.zone) {
        this.zone.runOutsideAngular(() => {
          this.showTimer = timer(8000).subscribe(() => {
            if (generation === this.widgetGeneration && !this.messengerVisible) void this.showUnavailableNotice()
          })
        })
      }
      this.sdkShowSpace('home')
    } catch (err) {
      this.showTimer?.unsubscribe()
      this.showTimer = undefined
      console.error('[IntercomService] show failed', err)
      void this.showUnavailableNotice()
    }
  }

  private async showUnavailableNotice (): Promise<void> {
    try {
      const notice = await this.toast.create({
        message: this.identityDenied
          ? 'Please sign out and back in to reconnect support chat. You can also visit support.salonmonster.com.'
          : "Chat couldn't load. Check your connection and reopen the app, or visit support.salonmonster.com.",
        duration: 6000,
        buttons: [{ text: 'Dismiss', role: 'cancel' }]
      })
      await notice.present()
    } catch {
    }
  }

  /**
   * Where the app is running. Capacitor tells iOS from Android; anything else
   * is the web app, including a desktop browser and mobile Safari/Chrome.
   */
  private currentPlatform (): ChatPlatform {
    try {
      if (this.platform.is('capacitor')) {
        return this.platform.is('ios') ? 'ios' : 'android'
      }
    } catch (err) {
      // Platform can throw before the app is ready; 'web' is the safe answer.
    }
    return 'web'
  }

  /**
   * Boots the Intercom messenger for the given stylist + salon session.
   *
   * Calls shutdown() first, defensively: if a previous logout's cleanup
   * failed (or never ran), booting on top of it would leak the previous
   * user's conversation history into this session. Shutdown clears the
   * Intercom session cookie before we identify the new user.
   */
  public boot (user: Stylist, salon: Salon): void {
    try {
      if (!this.shutdown() || !this.canIdentify(user)) return
      this.bootMessenger(user, salon)
    } catch (err) {
      console.error('[IntercomService] boot failed', err)
    }
  }

  private bootMessenger (user: Stylist, salon: Salon, refreshedJwt?: string): void {
    const settings: IntercomBootSettings = {
      app_id: INTERCOM_APP_ID,
      region: INTERCOM_REGION,
      user_id: String(user.getID()),
      // Launcher bubble stays hidden; chat opens via the app's own Help
      // buttons (show()).
      hide_default_launcher: true
    }

    if (refreshedJwt) {
      // Protected person attributes and company association travel only in the signed JWT.
      settings.intercom_user_jwt = refreshedJwt
      this.sdkBoot(settings)
      this.booted = true
      this.jwtSession = true
      return
    }

    const fullName = `${user.getFirstName() || ''} ${user.getLastName() || ''}`.trim()
    if (fullName) {
      settings.name = fullName
    }

    const email = user.getEmail()
    if (email) {
      settings.email = email
    }

    // Object form is required — Intercom ignores a flat avatar URL.
    const photoUrl = user.getPhotoUrl()
    if (photoUrl) {
      settings.avatar = {
        type: 'avatar',
        image_url: `${ENV_CONFIG.STAFF_THUMBNAIL_PHOTO_ROOT}/${photoUrl}`
      }
    }

    // NOTE: no user-level created_at — the stylist model has no signup-date
    // accessor (the old integration omitted it too), and faking it with the
    // salon's creation date would corrupt user-level cohorts.

    // Identity verification: unexpired JWT wins, hash is the fallback,
    // never both (see class doc).
    if (refreshedJwt || this.hasUnexpiredJwt(user)) {
      settings.intercom_user_jwt = refreshedJwt || user.intercomUserJwt
    } else if (user.intercomUserHash) {
      settings.user_hash = user.intercomUserHash
    }

    const company: Record<string, unknown> = {
      id: String(salon.getId()),
      name: salon.getName()
    }
    if (salon.getWebsite()) {
      company.website = salon.getWebsite()
    }
    const companyCreatedAt = this.toUnixSeconds(salon.getCreationDate())
    if (companyCreatedAt !== undefined) {
      // Intercom expects Unix SECONDS; a milliseconds value silently maps
      // to a date thousands of years out.
      company.created_at = companyCreatedAt
    }
    settings.company = company

    Object.assign(settings, buildChatCustomAttributes(user, salon, {
      platform: this.currentPlatform()
    }))
    settings['salon_creation_date'] = companyCreatedAt !== undefined
      ? new Date(companyCreatedAt * 1000).toISOString()
      : ''
    const jobTitle = getFilteredJobTitle(user)
    if (jobTitle) {
      settings['job_title'] = jobTitle
    }

    this.sdkBoot(settings)
    this.booted = true
    this.jwtSession = !!settings.intercom_user_jwt
  }

  /**
   * True when the stylist carries a usable identity credential (unexpired
   * JWT or the persisted hash). Sessions restored from a pre-Intercom
   * serialization may have neither; startSession fetches a fresh JWT rather
   * than attempting an unverified identified boot.
   */
  public canIdentify (user: Stylist): boolean {
    return this.hasUnexpiredJwt(user) || !!user.intercomUserHash
  }

  /** Shared credential predicate so boot() and canIdentify() cannot drift. */
  private hasUnexpiredJwt (user: Stylist): boolean {
    const jwt = user.intercomUserJwt
    try {
      return !!jwt && !this.isJwtExpired(jwt)
        && JSON.parse(this.base64UrlDecode(jwt.split('.')[1])).user_id === String(user.getID())
    } catch {
      return false
    }
  }

  /** Opens the messenger home space (help articles + start-a-conversation). */
  public show (): void {
    try {
      if (this.booted) {
        this.openMessenger()
        if (this.session && Date.now() - this.lastRefresh >= 5 * 60 * 1000) this.refreshIdentity()
      } else if (this.session?.token) {
        this.pendingShow = true
        this.refreshIdentity()
      } else {
        void this.showUnavailableNotice()
      }
    } catch (err) {
      console.error('[IntercomService] show failed', err)
    }
  }

  /**
   * Ends the Intercom session (clears the session cookie so the next user on
   * this device cannot see this user's conversations). Safe to call when the
   * messenger was never booted — the SDK no-ops/warns.
   *
   * @returns true when the SDK call did not throw, false otherwise — callers
   * (logout cleanup) use this to decide whether to log a cleanup failure.
   */
  public shutdown (): boolean {
    this.generation++
    this.identityDenied = false
    this.resetMessengerState()
    this.refreshRequest?.unsubscribe()
    this.refreshRequest = undefined
    this.refreshTimer?.unsubscribe()
    this.refreshTimer = undefined
    this.refreshFailures = 0
    this.session = undefined
    this.pendingShow = false
    this.lastRefresh = 0
    this.jwtSession = false
    try {
      this.sdkShutdown()
      this.booted = false
      return true
    } catch (err) {
      console.error('[IntercomService] shutdown failed', err)
      this.booted = false
      return false
    } finally {
      // Runs on both paths: a shutdown that threw is exactly when a stale
      // credential must not be left behind.
      this.scrubIdentitySettings()
    }
  }

  /**
   * Strips the previous user's identity fields from `window.intercomSettings`.
   *
   * The SDK's initialiser copies the whole boot payload onto that global —
   * credential included — and nothing clears it on shutdown, so without this
   * a logged-out user's JWT stays readable from the console for the rest of
   * the page's life. That is the one place the runtime-only handling the JWT
   * gets everywhere else (never persisted, never serialized) leaked.
   *
   * `app_id` / `api_base` deliberately stay: a widget script insert that is
   * still pending reads `app_id` off this global to build its URL.
   */
  private scrubIdentitySettings (): void {
    try {
      const settings = typeof window !== 'undefined'
        ? (window.intercomSettings as Record<string, unknown> | undefined)
        : undefined
      if (!settings) {
        return
      }
      for (const key of Object.keys(settings)) {
        if (!PRESERVED_SETTING_KEYS.includes(key)) {
          delete settings[key]
        }
      }
    } catch (err) {
      console.error('[IntercomService] settings scrub failed', err)
    }
  }

  /**
   * True when the JWT is expired, expires within the safety margin, or
   * cannot be decoded — malformed tokens are treated as expired so boot()
   * falls back to the user hash instead of sending garbage.
   */
  private isJwtExpired (jwt: string): boolean {
    try {
      const parts = jwt.split('.')
      if (parts.length !== 3) {
        return true
      }
      const payload = JSON.parse(this.base64UrlDecode(parts[1]))
      if (typeof payload.exp !== 'number') {
        return true
      }
      return payload.exp <= Date.now() / 1000 + JWT_EXPIRY_MARGIN_SECONDS
    } catch {
      return true
    }
  }

  private base64UrlDecode (segment: string): string {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
    return atob(padded)
  }

  /**
   * Converts a Date (or the date string a localStorage-restored Salon may
   * actually carry despite the model's `Date` type) to Unix seconds.
   * Returns undefined when absent or unparseable.
   */
  private toUnixSeconds (value: Date | string | number | null | undefined): number | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined
    }
    const date = value instanceof Date ? value : new Date(value)
    const ms = date.getTime()
    return isNaN(ms) ? undefined : Math.floor(ms / 1000)
  }

  // --- SDK seam -------------------------------------------------------------
  // The module-level SDK functions are called through these wrappers so specs
  // can spy on the boundary without a real widget. The `as unknown as` casts
  // pin our call signatures locally: 0.0.20's typings are loose and this
  // keeps compilation independent of them.

  /**
   * First boot: the default export sets window.intercomSettings (including
   * api_base derived from region) and injects the widget script, which boots
   * from those settings. Once the widget is installed, the default export
   * only issues reattach_activator + update — and 'update' cannot revive a
   * session after shutdown() — so subsequent boots (logout -> login on the
   * same page load) go straight to the SDK's named boot export instead,
   * skipping the wasted update round trip. `region` is a wrapper-only key
   * (the default export strips it); drop it before the named boot so it
   * never lands as a junk custom attribute.
   */
  private sdkBoot (settings: IntercomBootSettings): void {
    const alreadyInstalled = typeof window !== 'undefined' && typeof window.Intercom === 'function'
    if (alreadyInstalled) {
      const bootArgs: Record<string, unknown> = { ...settings }
      delete bootArgs.region
      ;(boot as unknown as (args: object) => void)(bootArgs)
    } else {
      (Intercom as unknown as (args: IntercomBootSettings) => void)(settings)
    }
  }

  private sdkOnShow (callback: () => void): void {
    onShow(callback)
  }

  private sdkOnHide (callback: () => void): void {
    onHide(callback)
  }

  private sdkUpdate (settings: Record<string, unknown>): void {
    (update as unknown as (args: object) => void)(settings)
  }

  private sdkShowSpace (space: string): void {
    (showSpace as unknown as (name: string) => void)(space)
  }

  /**
   * The SDK console-warns whenever `window.Intercom` is absent, and shutdown()
   * runs on every login and every logout — including sessions that never
   * boot Intercom at all (impersonation, no identity credential). Skip the call when the widget was never
   * installed, using the same predicate sdkBoot uses. A previous logout whose
   * cleanup failed still leaves `window.Intercom` a function, so the defensive
   * shutdown-before-boot path this guard sits behind is unaffected.
   */
  private sdkShutdown (): void {
    if (typeof window === 'undefined' || typeof window.Intercom !== 'function') {
      return
    }
    (shutdown as unknown as () => void)()
  }
}
